import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faAngleRight,
  faClock,
  faInfoCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useMainColor } from '@sorare/core/src/hooks/useMainColor';
import { isLightColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { PromotionalEvent_PromotionalEvent } from './__generated__/index.graphql.ts';

const Wrapper = styled(LinkBox)`
  display: flex;
  align-items: center;
  gap: var(--unit);
  padding: var(--unit) var(--double-unit);

  flex: 1;

  & > * {
    z-index: 1;
    &:last-child {
      margin-left: auto;
    }
  }
  position: relative;
  &:before {
    --blur: 4px;
    content: '';
    inset: calc(-1 * var(--blur));
    position: absolute;
    border-radius: inherit;
    z-index: 0;
    background:
      linear-gradient(0deg, var(--c-nl-300), var(--c-nl-300)),
      var(--background) no-repeat top / cover;
    filter: blur(var(--blur));
    pointer-events: none;
    opacity: 1;
    transition: opacity ease-in 0.1s;
  }
  &:hover {
    &:before {
      background:
        linear-gradient(0deg, var(--c-nl-100), var(--c-nl-100)),
        var(--background) no-repeat top / cover;
    }
  }
  & > *:first-child {
    display: none;
    @media ${tabletAndAbove} {
      display: block;
    }
  }
`;

const BannerWrapper = styled(LinkBox)`
  display: flex;
  align-items: center;
  gap: var(--unit);
  padding: var(--unit) var(--double-unit);
  background: var(--background) no-repeat top / cover;
`;

type Props = {
  promotionalEvent: PromotionalEvent_PromotionalEvent;
};

export const PromotionalEvent = ({ promotionalEvent }: Props) => {
  const { sport, generateSportContextPath } = useSportContext();

  const { pictureUrl, slug, blogUrl, secondaryBannerPictureUrl } =
    promotionalEvent;
  const mainColor = useMainColor(pictureUrl ?? undefined);

  if (!sport) {
    return null;
  }

  const tab =
    ('hasInstantBuy' in promotionalEvent && promotionalEvent.hasInstantBuy) ||
    isType(promotionalEvent, 'CartPurchaseRewardPromotionalEvent')
      ? generateSportContextPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY)
      : generateSportContextPath(ANY_SPORT_MARKET_SHOP_AUCTIONS);

  // FIXME
  // const color = getTextColorFromBackgroundColor(mainColor);
  const color = 'var(--c-white)';

  const WrapperComponent = secondaryBannerPictureUrl ? BannerWrapper : Wrapper;
  const backgroundUrl = secondaryBannerPictureUrl ?? pictureUrl;

  return (
    <WrapperComponent
      className="dark-mode"
      style={
        {
          '--background': `url(${backgroundUrl})`,
          color,
        } as React.CSSProperties
      }
    >
      <Chip color={isLightColor(mainColor) ? 'black' : 'transparent'}>
        <Horizontal>
          <FontAwesomeIcon icon={faClock} />
          <TimeLeft time={promotionalEvent.endDate} />
        </Horizontal>
      </Chip>

      <LinkOverlay
        as={Link}
        to={generatePath(
          `${tab}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${slug}#market`
        )}
      >
        <BodyS
          style={{
            fontFamily: 'var(--brand-font)',
            textTransform: 'uppercase',
            lineHeight: '14px',
          }}
          color={color}
        >
          {promotionalEvent.title}
        </BodyS>
      </LinkOverlay>

      {!!blogUrl && (
        <LinkOther as={Link} to={blogUrl}>
          <FontAwesomeIcon icon={faInfoCircle} />
        </LinkOther>
      )}

      <FontAwesomeIcon icon={faAngleRight} />
    </WrapperComponent>
  );
};

PromotionalEvent.fragments = {
  PromotionalEvent: gql`
    fragment PromotionalEvent_PromotionalEvent on PromotionalEventInterface {
      slug
      title
      pictureUrl
      endDate
      blogUrl
      secondaryBannerPictureUrl
      ... on MarketPromotionalEvent {
        slug
        hasInstantBuy
      }
      ... on SpecialEditionPromotionalEvent {
        slug
        hasInstantBuy
      }
    }
  ` as TypedDocumentNode<PromotionalEvent_PromotionalEvent>,
};
